import { vec2 } from "@tlaukkan/tsm";
import { Entity, World } from "ecsy";
import { ComponentElement } from "react";
import * as Components from "./components";
import { CompositionElement, getCompStat, getElementConstant, getElementStat, getEntityPlayer } from "./composition";
import { BehaviorsConstants, CollisionCategory, CellCategory, PhysicsConstants, Speeds, Timings, VirusSpawnerConstants, WeightClass, ShopConstants, Multiplayer } from "./constants";
import { Debug } from "./debug";
import { ElementDisplayInfo, getCellCategory, StatName } from "./elementDisplayInfo";
import { getEntityById } from "./entityFactory";
import { entityTrace } from "./entityTrace";
import { Game } from "./game";
import { GameElement } from "./gameElementTypes";
import { cellLocations, ciliaLocations, depthLocations, membraneLocations, glowLocations } from "./generated/assetLocations";
import { Rng } from "./rng";
import { Sound } from "./sound";
import { ArrayVec2 } from "./synchronization";
import { StateSystem } from "./systems/stateSystem";
import { TargetFilterType, TargetType, TriggerScope } from "./triggeredEffects";
import { addComponent, AssetLocation, degToRad, freezeComponent, getOrAddComponent, getSystem, pixelsToMeters, printObjectProperties, uniformVec2 } from "./utils";

export type EntityParameters = {
	creatorSyncId?: number
	targetPosition?: ArrayVec2,
	targetDirection?: ArrayVec2,
	spawnTargetType?: GameElement,
	spawnTargetFilter?: CollisionCategory,
	spawnExtraDelay?: number,
	spawnAmount?: number,
	spawnBattleAddedAmount?: number,
	effectRadius?: number,
	initialPush?: ArrayVec2,
	spawnWave?: number
	spawnLane?: number,
	spawnWaveCenter?: ArrayVec2,
	spawningPlayerId?: number,
	targetPlayerIds?: number[],
	hp?: number,
	size?: number,
	hasTiebreakerGrowth?: boolean
	wasKilled?: boolean
	copySourceCompElement?: CompositionElement
	isSplitChild?: boolean
}


export interface GameElementParameters {
	type: GameElement;
	position: vec2;
	playerId: number;
	rng: Rng;
	asset: any;
	promoted?: boolean,
	rotation?: number,
	id?: number,
	sourceCompElement?: CompositionElement,
	entityParameters?: EntityParameters,
}

export function createGameElement(world: World, params: GameElementParameters) {
	let entity = null;

	const rng = params.rng;
	const promoted = params.promoted;
	const info = ElementDisplayInfo.get(params.type);
	const assetName = info ? info.asset : "bloodcell";
	const location = cellLocations[assetName] as AssetLocation;
	const entityWidth = location.width;
	const entityHeight = location.height;
	const category = getCellCategory(params.type);

	const id = params.id === undefined ? -1 : params.id;
	let creatorId = -1;
	if (params.entityParameters && params.entityParameters.creatorSyncId !== undefined) {
		creatorId = params.entityParameters.creatorSyncId;
	}

	const stat = (name: StatName) => {
		if (params.sourceCompElement) {
			return getCompStat(params.sourceCompElement, name);
		}
		else if (params.entityParameters?.copySourceCompElement) {
			return getCompStat(params.entityParameters.copySourceCompElement, name);
		}
		else {
			return getElementStat(params.type, promoted || false, name);
		}
	}

	const constant = (name: string) => {
		const result = getElementConstant(params.type, promoted || false, name);
		if (result === undefined) {
			throw new Error(`Element ${GameElement[params.type]} has no constant ${name}`);
		}
		return result;
	}

	//console.log("Creating game element", GameElement[params.type], "with stats", info?.stats, "and sourceCompElement", params.sourceCompElement)

	switch (params.type) {
		case GameElement.SIMPLE:
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.AimForDirection, {});
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: stat('speed') });
			break;

		case GameElement.BUFFER_MAGNET:
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.Magnet);
			addComponent(entity, Components.EffectRadius, { radius: stat('effectRadius') });
			break;

		case GameElement.KINETIC_BUFFER_MAGNET:
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.Magnet);
			addComponent(entity, Components.EffectRadius, { radius: stat('effectRadius') });
			addComponent(entity, Components.OnMoveDistance, {
				distance: constant("distance"),
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ stat: 'effectRadius', value: constant("kineticIncrement") }] }),
					scope: TriggerScope.OneShot,
				}
			});
			break;

		case GameElement.CHAINER: {
			entity = createImmuneCell(world, params.position, WeightClass.Light);
			addComponent(entity, Components.Chainer, { maxLinks: constant("maxBonds") });
			break;
		}

		case GameElement.SWAPPER_CHAIN: {
			entity = createImmuneCell(world, params.position, WeightClass.Light);
			addComponent(entity, Components.Chainer, { maxLinks: 1 });
			addComponent(entity, Components.OnStartShop, {
				effect: {
					component: freezeComponent(Components.TradeHealth, { target: id, isPermanent: true }, { needsDeepCopy: true }),
					targetSelector: { type: TargetType.Chained },
					filters: [{ type: TargetFilterType.Nearest }],
					scope: TriggerScope.OneShot,
				}
			});
			break;
		}

		case GameElement.INFINITE_CHAINER: {
			entity = createImmuneCell(world, params.position, WeightClass.Light);
			addComponent(entity, Components.Chainer);
			addComponent(entity, Components.ConditionalEffect, {
				effect: freezeComponent(Components.Chainer, { maxLinks: constant("maxBonds") }, { needsDeepCopy: true }),
				targetSelector: { type: TargetType.Connected },
				filters: [{ type: TargetFilterType.NotSelf }]
			});
			break;
		}

		case GameElement.SPOTTER_CHAIN: {
			entity = createImmuneCell(world, params.position, WeightClass.Light);
			addComponent(entity, Components.Chainer, { maxLinks: constant("maxBonds") });
			addComponent(entity, Components.ConditionalEffect, {
				effect: freezeComponent(Components.TemporaryBuff, { buffs: [{ stat: 'range', value: constant("rangeIncrement") }] }, { needsDeepCopy: true }),
				targetSelector: { type: TargetType.Connected },
				filters: [{ type: TargetFilterType.NotSelf }]
			});
			break;
		}

		case GameElement.SCAPEGOAT_CHAIN: {
			entity = createImmuneCell(world, params.position, WeightClass.Light);
			addComponent(entity, Components.Chainer, { maxLinks: constant("maxBonds") });
			addComponent(entity, Components.ConditionalEffect, {
				effect: freezeComponent(Components.HealthSiphon, { victim: entity }),
				targetSelector: { type: TargetType.Connected },
				filters: [{ type: TargetFilterType.NotSelf }]
			});
			break;
		}

		case GameElement.FAST_SEEKER: {
			const range = stat('range');
			const taggedRange = range * 2;
			const cooldown = stat('cooldown');
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ImpulseMotion, { force: Speeds.FastImpulse, canRepeatOnCooldown: true });
			addComponent(entity, Components.Cooldown, { cooldown });
			addComponent(entity, Components.OrientationControl);
			addComponent(entity, Components.VisibleRange, { range, taggedRange, filter: CollisionCategory.Virus | CollisionCategory.Virophage, sticky: true });
			addComponent(entity, Components.AimForTarget, { noTargetBehavior: Components.NoTargetBehavior.ReturnHome });
			break;
		}

		case GameElement.SEEKER: {
			const range = stat('range');
			const taggedRange = range * 2;
			const speed = stat('speed');
			const accel = speed * 5; //reach max speed in ~6 frames given 60fps
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed, accel });
			addComponent(entity, Components.OrientationControl);
			addComponent(entity, Components.VisibleRange, { range, taggedRange, filter: CollisionCategory.Virus | CollisionCategory.Virophage, sticky: true });
			addComponent(entity, Components.AimForTarget, { noTargetBehavior: Components.NoTargetBehavior.ReturnHome });

			break;
		}

		case GameElement.TAG_SEEKER_SPLITTER: {
			const range = stat('range');
			const taggedRange = range;
			const speed = stat('speed');
			const accel = speed * 5; //reach max speed in ~6 frames given 60fps
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed, accel });
			addComponent(entity, Components.OrientationControl);
			addComponent(entity, Components.VisibleRange, { range, taggedRange, filter: CollisionCategory.Virus | CollisionCategory.Virophage, sticky: true });
			addComponent(entity, Components.AimForTarget, { noTargetBehavior: Components.NoTargetBehavior.ReturnHome });

			addComponent(entity, Components.LowPriorityTarget, { category: CollisionCategory.Cytokine, element: GameElement.INTERFERON });
			addComponent(entity, Components.Consumer, { types: [Components.ConsumableType.Interferon] });
			addComponent(entity, Components.OnConsume, {
				requiredAmount: [{ type: Components.ConsumableType.Interferon, amount: constant('tagAmount') }],
				effect: {
					component: freezeComponent(Components.ReplaceSelf, { numSplits: 2, initialPush: Speeds.WeakImpulse * 2.5 }),
					scope: TriggerScope.OneShot,
				}
			});
			break;
		}

		case GameElement.MINELAYER: {
			const range = stat('range');
			const taggedRange = range * 2;
			const speed = stat('speed');
			const accel = speed * 5; //reach max speed in ~6 frames given 60fps
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed, accel });
			addComponent(entity, Components.OrientationControl);

			addComponent(entity, Components.VisibleRange, { range, taggedRange, filter: CollisionCategory.Virus | CollisionCategory.Virophage, sticky: true });
			addComponent(entity, Components.AimForTarget, { noTargetBehavior: Components.NoTargetBehavior.ReturnHome });
			addComponent(entity, Components.OnMoveDistance, {
				distance: constant('dropDistance'),
				effect: {
					component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.BULLET }] }),
					targetSelector: { type: TargetType.Self },
					scope: TriggerScope.OneShot
				}
			});
			break;
		}

		case GameElement.TAG_PROOF_SEEKER: {
			const range = stat('range');
			const taggedRange = range * 2;
			const speed = stat('speed');
			const accel = speed * 5; //reach max speed in ~6 frames given 60fps
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed, accel });
			addComponent(entity, Components.OrientationControl);

			addComponent(entity, Components.VisibleRange, { range, taggedRange, filter: CollisionCategory.Virus | CollisionCategory.Virophage, sticky: true });
			addComponent(entity, Components.AimForTarget, { noTargetBehavior: Components.NoTargetBehavior.ReturnHome });
			addComponent(entity, Components.TagProof);
			break;
		}

		case GameElement.TAG_SPRAYER: {
			const range = stat('range');
			const cooldown = stat('cooldown');
			const shots = constant('tagAmount');
			entity = createImmuneCell(world, params.position);
			const impulse = Math.sqrt(range * PhysicsConstants.DefaultDamping * 2); //roughly estimate friction stuff;
			addComponent(entity, Components.SprinklerMotion);
			addComponent(entity, Components.Shoot, { ammo: GameElement.INTERFERON, initialImpulse: impulse, shots });
			addComponent(entity, Components.Cooldown, { cooldown, remaining: cooldown });
			break;
		}

		case GameElement.TAG_DRAG_DROPPER: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.DragDropProtein, {
				triggers: constant("tagAmount"),
				distance: constant("dropDistance"),
				proteinType: GameElement.INTERFERON,
			});
			break;
		}

		case GameElement.INCOME: {
			entity = createImmuneCell(world, params.position);
			const income = constant("incomeIncrement");
			addComponent(entity, Components.OnSurvive, {
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ stat: 'sellValue', value: income, isPermanent: true }] }),
					scope: TriggerScope.OneShot,
				}
			});
			break;
		}

		case GameElement.ORBITER: {
			const speed = stat('speed');
			entity = createImmuneCell(world, params.position, WeightClass.Heavy);
			addComponent(entity, Components.AimOrbit, { orbitOwner: true });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed });
			addComponent(entity, Components.TorqueOrientationControl);
			break;
		}

		case GameElement.SPINNER: {
			const speed = stat('speed');
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.AimOrbit, { orbitOwner: true });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed });
			addComponent(entity, Components.Chainer, { maxLinks: 1 });
			break;
		}

		case GameElement.MARTYR: {
			entity = createImmuneCell(world, params.position);
			const healing = constant("healAmount");
			const effect = {
				component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.HEAL_WAVE, params: { hp: healing } }] }),
				scope: TriggerScope.OneShot,
			};
			addComponent(entity, Components.OnDeath, { effect });
			addComponent(entity, Components.EffectRadius, { radius: stat('effectRadius') });
			break;
		}

		case GameElement.HEALER: {
			const cooldown = stat('cooldown');
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.Spawner, { amount: Infinity, spawns: [{ type: GameElement.HEAL_WAVE, params: { hp: constant("healAmount") } }] })
			addComponent(entity, Components.EffectRadius, { radius: stat('effectRadius') });
			addComponent(entity, Components.Cooldown, { cooldown });
			break;
		}

		case GameElement.BCELL: {
			const cooldown = stat('cooldown');
			const speed = stat('speed');
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.Spawner, { amount: Infinity, spawns: [{ type: GameElement.INTERFERON, }] })
			addComponent(entity, Components.Cooldown, { cooldown });
			//addComponent(entity, Components.AimForPosition, { moveTowardsTarget: false, target: new vec2(targetPos) });
			addComponent(entity, Components.AimForDirection);
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed });
			break;
		}

		case GameElement.PUSHER: {
			const cooldown = stat('cooldown');
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.Spawner, { amount: Infinity, spawns: [{ type: GameElement.FORCE_EXPLOSION, }] })
			addComponent(entity, Components.Cooldown, { cooldown });
			addComponent(entity, Components.OrientationControl);
			addComponent(entity, Components.EffectRadius, { radius: stat('effectRadius') })
			break;
		}

		case GameElement.BOMB:
			entity = createImmuneCell(world, params.position);
			const effect = {
				component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.EXPLOSION }] }),
				scope: TriggerScope.OneShot,
			};
			addComponent(entity, Components.OnDeath, { effect });
			addComponent(entity, Components.EffectRadius, { radius: stat('effectRadius') });
			break;

		case GameElement.GROW_BOMB: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.OnSurvive, {
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ stat: 'effectRadius', value: constant("blastIncrement"), isPermanent: true }] }),
					scope: TriggerScope.OneShot,
				}
			});
			const effect = {
				component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.EXPLOSION }] }),
				scope: TriggerScope.OneShot,
			};
			addComponent(entity, Components.EffectRadius, { radius: stat('effectRadius') });
			addComponent(entity, Components.OnDeath, { effect });
			break;
		}

		case GameElement.CONSUME_BOMB: {
			entity = createImmuneCell(world, params.position);
			const effect = {
				component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.EXPLOSION }] }),
				scope: TriggerScope.OneShot,
			};
			addComponent(entity, Components.OnDeath, { effect });
			addComponent(entity, Components.EffectRadius, { radius: stat('effectRadius') });

			//Consumer test code delete me later
			addComponent(entity, Components.Consumer, { types: [Components.ConsumableType.CapsidProtein], });
			addComponent(entity, Components.OnConsume, {
				requiredAmount: [{ type: Components.ConsumableType.CapsidProtein, amount: constant("consumeAmount") }],
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ stat: 'effectRadius', value: constant("blastIncrement"), isPermanent: false }] }),
					scope: TriggerScope.OneShot,
				}
			});
			break;
		}

		case GameElement.SHOOTER: {
			const cooldown = stat('cooldown');
			const range = stat('range');
			const taggedRange = range * 2;
			entity = createImmuneCell(world, params.position);
			const remaining = (rng.next() * cooldown) / 2;
			addComponent(entity, Components.VisibleRange, { type: Components.RangeType.Line, filter: CollisionCategory.Virophage | CollisionCategory.Virus, range, taggedRange });
			addComponent(entity, Components.LowPriorityTarget, { category: CollisionCategory.Antibody, consumerOf: Components.ConsumableType.Bullet });
			addComponent(entity, Components.Shoot, { aim: false, fireOnSight: true });
			addComponent(entity, Components.Cooldown, { cooldown, remaining })
			addComponent(entity, Components.OrientationControl);
			break;
		}

		case GameElement.WIND_SHOOTER: {
			const cooldown = stat('cooldown');
			entity = createImmuneCell(world, params.position);
			const remaining = (rng.next() * cooldown) / 2;
			addComponent(entity, Components.Shoot, { aim: false });
			addComponent(entity, Components.Cooldown, { cooldown, remaining });
			addComponent(entity, Components.OnKill, {
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ stat: "cooldown", value: -constant("reduction") }] }),
					targetSelector: { type: TargetType.Self },
					scope: TriggerScope.OneShot,
				},
			});
			addComponent(entity, Components.OrientationControl);
			break;
		}

		case GameElement.AIM_SHOOTER: {
			const range = stat('range');
			const taggedRange = range * 2;
			const cooldown = stat('cooldown');
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.OrientationControl, { turnSpeed: 2 });
			addComponent(entity, Components.VisibleRange, {
				range, taggedRange, type: Components.RangeType.Circle,
				filter: CollisionCategory.Virophage | CollisionCategory.Virus,
			});
			addComponent(entity, Components.LowPriorityTarget, { category: CollisionCategory.Antibody, consumerOf: Components.ConsumableType.Bullet });
			addComponent(entity, Components.Shoot, { aim: true });

			addComponent(entity, Components.Cooldown, { cooldown, remaining: cooldown });
			break;
		}

		case GameElement.BIG_SHOOTER: {
			const range = stat('range');
			const taggedRange = range * 2;
			const cooldown = stat('cooldown');
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.OrientationControl);
			addComponent(entity, Components.VisibleRange, {
				range, taggedRange, type: Components.RangeType.Circle,
				filter: CollisionCategory.Virophage | CollisionCategory.Virus,
				sticky: true
			});
			addComponent(entity, Components.LowPriorityTarget, { category: CollisionCategory.Antibody, consumerOf: Components.ConsumableType.Bullet });
			addComponent(entity, Components.Shoot, { aim: true, scale: true });

			addComponent(entity, Components.Cooldown, { cooldown, remaining: cooldown });
			break;
		}

		case GameElement.WALL:
			entity = createImmuneCell(world, params.position);
			break;

		case GameElement.MONEY_WALL: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.OnStartBattle, {
				effect: {
					component: freezeComponent(Components.SetHealthEqualMoney, { convertionRate: constant("conversionRate") }),
					scope: TriggerScope.OneShot
				}
			});
			break;
		}

		case GameElement.INTEREST: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.OnStartBattle, {
				effect: {
					component: freezeComponent(Components.GainInterest, { amount: constant("interestRate") }),
					scope: TriggerScope.OneShot
				}
			});
			break;
		}

		case GameElement.MONEY_PRINTER: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.Consumer, {
				types: [
					Components.ConsumableType.CapsidProtein,
				]
			});
			addComponent(entity, Components.OnConsume, {
				requiredAmount: [
					{ type: Components.ConsumableType.CapsidProtein, amount: constant("proteinAmount") },
				],
				effect: {
					component: freezeComponent(Components.GainMoney, { amount: constant("incomeIncrement") }),
					scope: TriggerScope.OneShot
				}
			});
			break;
		}
		case GameElement.MONEY_BOMB_TURRET: {
			entity = createImmuneCell(world, params.position);
			const range = stat('range');
			const taggedRange = range * 2;
			const cooldown = stat('cooldown');
			addComponent(entity, Components.OrientationControl);
			addComponent(entity, Components.VisibleRange, {
				range, taggedRange, type: Components.RangeType.Circle,
				filter: CollisionCategory.Virophage | CollisionCategory.Virus,
				sticky: true
			});
			addComponent(entity, Components.Shoot, { aim: true, ammo: GameElement.BOMB, initialImpulse: Speeds.NormalImpulse });
			addComponent(entity, Components.Cooldown, { cooldown, remaining: cooldown });
			addComponent(entity, Components.ShotPrice, { amount: constant("carbCost") });
			break;
		}

		case GameElement.COLOR_GROW_WALL:
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.OnBuyOther, {
				categoryFilter: CellCategory.BCell,
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ isPermanent: true, stat: "health", value: constant("healthIncrement") }] }),
					targetSelector: { type: TargetType.SelfIfOther },
					filters: [{ type: TargetFilterType.Category, category: CellCategory.BCell }],
					scope: TriggerScope.OneShot,
				},
			});
			break;

		case GameElement.THORN_WALL: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.OnDamage, {
				effect: [
					{
						component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.BULLET, initialPush: Speeds.FastImpulse, headingRotation: 1.5708, offset: new vec2([-1, 0]) }] }),
						scope: TriggerScope.OneShot,
					},
					{
						component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.BULLET, initialPush: Speeds.FastImpulse, headingRotation: -1.5708, offset: new vec2([1, 0]) }] }),
						scope: TriggerScope.OneShot,
					}
				]
			});
			break;
		}

		case GameElement.BULLET_WALL: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.Consumer, {
				types: [Components.ConsumableType.Bullet]
			});
			addComponent(entity, Components.OnConsume, {
				cooldown: 0.05,
				requiredAmount: [
					{ type: Components.ConsumableType.Bullet, amount: 1 }
				],
				effect: [
					{
						component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.BULLET, initialPush: Speeds.FastImpulse, headingRotation: 1.5708, offset: new vec2([-1, 0]) }] }),
						scope: TriggerScope.OneShot,
					},
					{
						component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.BULLET, initialPush: Speeds.FastImpulse, headingRotation: -1.5708, offset: new vec2([1, 0]) }] }),
						scope: TriggerScope.OneShot,
					}
				]
			});
			break;
		}

		case GameElement.SHRAPNEL: {
			entity = createImmuneCell(world, params.position);
			const radius = stat('effectRadius');
			addComponent(entity, Components.Consumer, {
				types: [Components.ConsumableType.Bullet]
			});
			const effect = freezeComponent(Components.ScatterProtein, { spreadRadiens: degToRad(15), targetViruses: true, range: radius });
			addComponent(entity, Components.OnDeath, {
				effect: {
					component: effect,
					scope: TriggerScope.OneShot,
				},
			});
			addComponent(entity, Components.EffectRadius, { radius });
			break;
		}

		case GameElement.COLOR_BUFFER: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.OnBuyOther, {
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ isPermanent: true, stat: "health", value: constant("healthIncrement"), sourceEntity: params.id }] }),
					targetSelector: { type: TargetType.Other },
					scope: TriggerScope.OneShot,
					filters: [{ type: TargetFilterType.ImmuneCell }, { type: TargetFilterType.Category, category: CellCategory.BCell }]
				}
			});
			break;
		}

		case GameElement.COLOR_NEAREST_BUFFER: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.OnBuy, {
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ isPermanent: true, stat: "health", value: constant("healthIncrement"), sourceEntity: params.id }] }),
					targetSelector: { type: TargetType.Team },
					scope: TriggerScope.OneShot,
					filters: [
						{ type: TargetFilterType.Category, category: CellCategory.BCell },
						{ type: TargetFilterType.NotSelf },
						{ type: TargetFilterType.Nearest }
					]
				}
			});
			break;
		}

		case GameElement.COLOR_BOOSTER: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.HealBooster, {
				amount: constant("healthIncrement"),
				categoryFilter: CellCategory.BCell,
			});
			break;
		}

		case GameElement.SEEKER_FACTORY:
			entity = createImmuneCell(world, params.position, WeightClass.Heavy);
			addComponent(entity, Components.Consumer, {
				types: [
					Components.ConsumableType.CapsidProtein,
					Components.ConsumableType.Bullet,
				]
			});
			addComponent(entity, Components.OnConsume, {
				requiredAmount: [
					{ type: Components.ConsumableType.CapsidProtein, amount: constant("capsid") },
					{ type: Components.ConsumableType.Bullet, amount: constant("bullet") }
				],
				effect: {
					component: freezeComponent(Components.SpawnOnce, {
						spawns: [{
							type: GameElement.SEEKER,
							initialPush: Speeds.FastImpulse,
						}]
					}),
					scope: TriggerScope.OneShot,
				}
			});
			break;

		case GameElement.COOLDOWN_ACCELLERATOR:
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.Chainer, { maxLinks: 1 })
			addComponent(entity, Components.Consumer, {
				types: [Components.ConsumableType.CapsidProtein,]
			});
			addComponent(entity, Components.OnConsume, {
				requiredAmount: [
					{ type: Components.ConsumableType.CapsidProtein, amount: constant("consume") },
				],
				effect: {
					//Give a cooldown reserve component to chained cells. 
					component: freezeComponent(Components.CooldownReserve, { reserve: constant("seconds") }),
					targetSelector: { type: TargetType.Chained },
					scope: TriggerScope.Permanent,
				}
			});
			break;

		case GameElement.MORTAR:
			entity = createImmuneCell(world, params.position, WeightClass.Heavy);
			addComponent(entity, Components.Consumer, {
				types: [Components.ConsumableType.Bullet,]
			});
			addComponent(entity, Components.OnConsume, {
				requiredAmount: [
					{ type: Components.ConsumableType.Bullet, amount: constant("consume") },
				],
				cooldown: 0.2,
				effect: {
					//Give a cooldown reserve component to chained cells. 
					component: freezeComponent(Components.SpawnOnce, {
						spawns: [{
							type: GameElement.BOMB,
							promoted,
							initialPush: Speeds.FastImpulse,
						}]
					}),
					targetSelector: { type: TargetType.Self },
					scope: TriggerScope.OneShot,
				}
			});
			break;
		case GameElement.KINETIC_CHAIN_BUFFER: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.Chainer, { maxLinks: constant("maxBonds") });
			addComponent(entity, Components.OnMoveDistance, {
				distance: constant("distance"),
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ stat: 'health', value: constant("healthIncrement"), sourceEntity: params.id }] }),
					targetSelector: { type: TargetType.Connected },
					scope: TriggerScope.OneShot,
				}
			});
			break;
		}

		case GameElement.WILDCARD:
			entity = createImmuneCell(world, params.position, WeightClass.Heavy);
			break;

		case GameElement.SWAPPER: {
			const range = stat('range');
			const taggedRange = 0;
			const speed = stat('speed');
			const accel = speed * 5; //reach max speed in ~6 frames given 60fps
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed, accel });
			addComponent(entity, Components.OrientationControl);
			addComponent(entity, Components.VisibleRange, { range, taggedRange, filter: CollisionCategory.Antibody, sticky: false, excludeList: new Set(), resetExcludeList: false });
			addComponent(entity, Components.AimForTarget);
			addComponent(entity, Components.TradeHealthOnHit);
			break;
		}

		case GameElement.TAG_ORBITER: {
			const speed = stat('speed');
			entity = createImmuneCell(world, params.position, WeightClass.Heavy);
			addComponent(entity, Components.AimOrbit, { orbitOwner: true });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed });
			addComponent(entity, Components.TorqueOrientationControl);
			const cooldown = stat('cooldown');
			addComponent(entity, Components.Spawner, { amount: Infinity, spawns: [{ type: GameElement.INTERFERON, }] })
			addComponent(entity, Components.Cooldown, { cooldown });
			break;
		}

		case GameElement.PIERCE_SHOOTER: {
			entity = createImmuneCell(world, params.position);
			const cooldown = stat('cooldown');
			const range = stat('range');
			const taggedRange = range * 2;
			const remaining = (rng.next() * cooldown) / 2;
			addComponent(entity, Components.VisibleRange, { type: Components.RangeType.Line, filter: CollisionCategory.Virophage | CollisionCategory.Virus, range, taggedRange });
			addComponent(entity, Components.LowPriorityTarget, { category: CollisionCategory.Antibody, consumerOf: Components.ConsumableType.Bullet });
			addComponent(entity, Components.Shoot, { aim: false, fireOnSight: true, shotHealth: constant("shotHealth") });
			addComponent(entity, Components.Cooldown, { cooldown, remaining })
			addComponent(entity, Components.OrientationControl);
			break;
		}

		case GameElement.HEAVY_CHAINER: {
			entity = createImmuneCell(world, params.position, WeightClass.SuperHeavy);
			addComponent(entity, Components.Chainer, { maxLinks: constant("maxBonds") });
			break;
		}

		case GameElement.STEM_CELL: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.VisibleRange, {
				type: Components.RangeType.Line,
				range: constant('range'),
				filter: CollisionCategory.Antibody,
				excludeElements: [GameElement.STEM_CELL]
			});
			addComponent(entity, Components.ReplaceSelf, { spawnTypeSource: "nearest", initialPush: Speeds.WeakImpulse * 0.01 });
			break;
		}

		case GameElement.SENTRY_ORBITER: {
			const speed = stat('speed');
			entity = createImmuneCell(world, params.position, WeightClass.Heavy);
			addComponent(entity, Components.Sentry, { maxSpeed: speed, orbitOwner: true });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: speed });
			addComponent(entity, Components.TorqueOrientationControl);

			break;
		}

		//Blood cells

		case GameElement.BLOODCELL:
			entity = createBloodCell(world, params.position);
			if (params.entityParameters?.wasKilled) {
				addComponent(entity, Components.Submerged, { percentSubmerged: 1.0, dontAffectScale: true });
			}
			break;

		case GameElement.INFECTED_BLOODCELL: {
			entity = world.createEntity();
			addComponent(entity, Components.Renderable, {
				sprite: undefined,
				assetLocation: cellLocations.infected_bloodcell,
				depthAssetLocation: depthLocations.infected_bloodcell,
				membraneAssetLocation: membraneLocations.infected_bloodcell,
				glowAssetLocation: glowLocations.infected_bloodcell
			});
			const size = pixelsToMeters(entityWidth);
			addComponent(entity, Components.Physics, {
				initialPosition: params.position,
				size: uniformVec2(size),
				baseSize: uniformVec2(size),
			});
			const spawnArgs = params.entityParameters || { spawnTargetType: GameElement.VIRUS, spawnAmount: 3, spawnBattleAddedAmount: 0 }
			const targetPlayers = params.entityParameters!.targetPlayerIds!;

			const type = spawnArgs.spawnTargetType !== undefined ? spawnArgs.spawnTargetType : GameElement.VIRUS;
			addComponent(entity, Components.Spawner, {
				spawns: [{
					type,
				}],
				amount: (spawnArgs.spawnAmount || 0) + (spawnArgs.spawnBattleAddedAmount || 0),
			});
			const min = VirusSpawnerConstants.SpawnIntervalMin;
			const variance = (VirusSpawnerConstants.SpawnIntervalMax - min) * rng.next();
			let initialDelay = params.entityParameters?.spawnExtraDelay || 0;
			if (initialDelay === -1) initialDelay = Infinity;
			addComponent(entity, Components.Cooldown, { cooldown: min + variance, extra: initialDelay });
			//extra: Infinity, essentially disable the spawner til wave timing code enables it
			addComponent(entity, Components.Infected, { targetPlayerIds: targetPlayers });
			const state = getSystem(world, StateSystem).getCurrentState().toLowerCase();
			if (state === "shop") {
				addComponent(entity, Components.Submerged, { surfacing: true, percentSubmerged: 1.0, rate: 1.5 });
			}
			addComponent(entity, Components.Hoverable);

			if (params.entityParameters?.hasTiebreakerGrowth) {
				addComponent(entity, Components.InfectionGrowth, { wave: params.entityParameters.spawnWave });
			}
			const spawnLane = params.entityParameters?.spawnLane;
			if (spawnLane !== undefined) {
				addComponent(entity, Components.LaneInfo, { lane: spawnLane });
			}
			break;
		}

		case GameElement.ELECTRIC: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ElectricCharge, { charge: constant("charge") });
			break;
		}

		case GameElement.ELECTRIC_GENERATOR_WALL: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ElectricCharge, { charge: 0 }); //this makes the preview show even though no charge at start
			addComponent(entity, Components.OnCollision, {
				componentFilter: [Components.Virus, Components.Antibody],
				currentCooldown: 0.1,
				cooldown: 0.1,
				perEntityCooldown: 0.25,
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ stat: "charge", value: constant("chargeIncrement") }] }),
					scope: TriggerScope.OneShot,
				}
			})
			break;
		}

		case GameElement.ELECTRIC_KINETIC_GENERATOR: {
			entity = createImmuneCell(world, params.position, WeightClass.Light);
			addComponent(entity, Components.ElectricCharge, { charge: 0 }); //this makes the preview show even though no charge at start
			addComponent(entity, Components.OnMoveDistance, {
				distance: constant("distance"),
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ stat: "charge", value: constant("kineticIncrement") }] }),
					scope: TriggerScope.OneShot,
				}
			});
			break;
		}

		case GameElement.ELECTROMAGNET: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ElectricCharge, { charge: 0 }); //required for electric draw
			addComponent(entity, Components.ElectricSink);
			addComponent(entity, Components.Magnet);
			addComponent(entity, Components.EffectRadius, { radius: 0 });
			addComponent(entity, Components.ConvertChargeToEffectRadius, { conversionRate: constant("conversionRate") });
			break;
		}

		case GameElement.ELECTRIC_HUNGRY_GENERATOR: {
			entity = createImmuneCell(world, params.position);
			addComponent(entity, Components.ElectricCharge, { charge: 0 }); //this makes the preview show even though no charge at start
			addComponent(entity, Components.Consumer, {
				types: [
					Components.ConsumableType.CapsidProtein,
				]
			});
			addComponent(entity, Components.OnConsume, {
				requiredAmount: [
					{ type: Components.ConsumableType.CapsidProtein, amount: constant("proteinAmount") },
				],
				effect: {
					component: freezeComponent(Components.Buff, { buffs: [{ stat: "charge", value: constant("increment") }] }),
					scope: TriggerScope.OneShot,
				},
			});
			break;
		}

		//Viruses

		case GameElement.VIRUS: {
			entity = createVirus(world, params.position);
			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: false, playerId });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: Speeds.Normal })
			break;
		}

		case GameElement.JUMPER: {
			entity = createVirus(world, params.position);
			addComponent(entity, Components.Jump, { activePeriod: 0.1, jumpForce: Speeds.NormalImpulse });
			addComponent(entity, Components.Cooldown, { cooldown: 1, remaining: 1 })
			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: false, playerId }); //seeker would override
			addComponent(entity, Components.VisibleRange, { range: 30, filter: CollisionCategory.Cell, sticky: false })
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: Speeds.VerySlow });
			addComponent(entity, Components.OrientationControl);
			break;
		}
		case GameElement.ANTIBODY_SEEKER: {
			entity = createVirus(world, params.position);

			addComponent(entity, Components.AimForTarget, { noTargetBehavior: Components.NoTargetBehavior.Drift });
			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: false, playerId }); //seeker would override

			addComponent(entity, Components.VisibleRange, { range: 15, filter: CollisionCategory.Antibody, sticky: false })
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: Speeds.Fast, accel: Speeds.Fast * 10 });
			addComponent(entity, Components.OrientationControl);
			break;
		}

		case GameElement.SPLITTER: {
			entity = createVirus(world, params.position);
			const wasCreatedBySplitter = params.entityParameters?.creatorSyncId !== undefined;
			const isSplitChild = params.entityParameters?.isSplitChild === true;

			if (wasCreatedBySplitter && !isSplitChild) {
				const effect = {
					component: freezeComponent(Components.ReplaceSelf, {
						numSplits: 2,
						initialPush: Speeds.WeakImpulse * 1,
						spreadRadians: degToRad(20),
					}),
					scope: TriggerScope.OneShot,
				}
				addComponent(entity, Components.OnNearTarget, { thresholdDistance: 40, effect });
			}

			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: false, playerId });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: Speeds.Normal })
			addComponent(entity, Components.OrientationControl); //face travel direction so splits head the right way
			break;
		}

		case GameElement.AVOIDER: {
			entity = createVirus(world, params.position);
			const targeting = addComponent(entity, Components.Targeting);
			const antibodyTargetingIndex = targeting.addSpec(Components.RangeType.Circle, 15, CollisionCategory.Antibody, 0, false, false, 0);
			addComponent(entity, Components.AvoidMotion, { antibodyTargetingIndex, force: 150 });
			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: false, playerId });

			addComponent(entity, Components.ContinuousMotion, { maxSpeed: 20 })
			break;
		}

		case GameElement.WAVE: {
			entity = createVirus(world, params.position);
			addComponent(entity, Components.SineWaveMotion, { period: 2.8, accel: Speeds.Fast * 1.0, slope: 15 });
			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: true, playerId, disableAfterOneAim: true });
			break;
		}

		case GameElement.VIRUS_PUSHER: {
			entity = createVirus(world, params.position);
			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: false, playerId });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: Speeds.Normal })
			const effect = {
				component: freezeComponent(Components.SpawnOnce, {
					spawns: [{
						type: GameElement.FORCE_EXPLOSION,
						params: {
							spawnTargetFilter: CollisionCategory.Antibody
						}
					}]
				}),
				scope: TriggerScope.OneShot,
			};
			addComponent(entity, Components.OnDeath, { effect });
			addComponent(entity, Components.EffectRadius, { radius: 6 });
			break;
		}

		case GameElement.VIROPHAGE: {
			entity = createVirus(world, params.position);
			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: false, playerId });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: Speeds.Normal })
			addComponent(entity, Components.Consumer, { types: [Components.ConsumableType.CapsidProtein], range: 10 });
			addComponent(entity, Components.OnConsume, {
				requiredAmount: [{ type: Components.ConsumableType.CapsidProtein, amount: 1 }],
				effect: [
					{
						component: freezeComponent(Components.Buff, { buffs: [{ stat: "health", value: 1 }] }),
						targetSelector: { type: TargetType.Self },
						scope: TriggerScope.OneShot,
					}, {
						component: freezeComponent(Components.Grow),
						targetSelector: { type: TargetType.Self },
						scope: TriggerScope.OneShot,
					}
				]
			});

			break;
		}

		case GameElement.SWARMER: {
			entity = createVirus(world, params.position);
			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: false, playerId });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: Speeds.Normal })
			addComponent(entity, Components.VirusPointAttractor);

			break;
		}

		case GameElement.AOE_VIRUS: {
			entity = createVirus(world, params.position);
			const playerId = params.entityParameters ? params.entityParameters.spawningPlayerId : -1;
			addComponent(entity, Components.AimForPlayer, { onlyTargetOnce: false, playerId });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: Speeds.Normal })
			const damage = constant("damage");
			const effect = {
				component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.HARM_WAVE, params: { hp: damage } }] }),
				scope: TriggerScope.OneShot,
			};
			addComponent(entity, Components.OnDeath, { effect });
			addComponent(entity, Components.EffectRadius, { radius: stat('effectRadius') });

			//Don't do any damage to immune cells on collisions, the harm wave will do that
			const attacker = getOrAddComponent(entity, Components.Attacker);
			attacker.damageType = Components.DamageType.OnlyInfect;
			break;
		}

		//AOE Effects
		case GameElement.EXPLOSION:
			entity = createExplosion(world, params.position, params.entityParameters?.effectRadius);
			break;

		case GameElement.FORCE_EXPLOSION:
			entity = createForceExplosion(world, params.position, params.entityParameters?.creatorSyncId || -1, params.entityParameters);
			break;

		case GameElement.HEAL_WAVE:
			entity = createHealWave(world, params.position, params.entityParameters?.creatorSyncId || -1, params.entityParameters);
			break;

		case GameElement.HARM_WAVE:
			entity = createHarmWave(world, params.position, params.entityParameters?.creatorSyncId || -1, params.entityParameters);
			break;

		//Resources
		case GameElement.BULLET: {
			const targetDir = params.entityParameters?.targetDirection;
			entity = createBullet(world, params.position, !targetDir ? null : new vec2(targetDir), params?.entityParameters?.creatorSyncId, params?.entityParameters?.hp, params?.entityParameters?.size);
			break;
		}

		case GameElement.INTERFERON:
			const range = BehaviorsConstants.TagProteinAttractRange;
			entity = world.createEntity();
			addComponent(entity, Components.Physics, {
				initialPosition: params.position,
				size: uniformVec2(0.5),
				baseSize: uniformVec2(0.5),
				isSensor: true
			});
			addComponent(entity, Components.Renderable, {
				sprite: undefined,
				assetLocation: cellLocations.interferon,
				depthAssetLocation: depthLocations.interferon,
				glowAssetLocation: glowLocations.interferon
			});
			addComponent(entity, Components.Cytokine);
			addComponent(entity, Components.Transform, { position: params.position });
			addComponent(entity, Components.VisibleRange, {
				range,
				filter: CollisionCategory.Virus | CollisionCategory.Virophage,
				taggedRange: -1,
			});
			addComponent(entity, Components.LowPriorityTarget, { category: CollisionCategory.Antibody, consumerOf: Components.ConsumableType.Interferon });
			addComponent(entity, Components.ContinuousMotion, { maxSpeed: Speeds.Fast, accel: Speeds.Fast * 5 });
			addComponent(entity, Components.AimForTarget, { noTargetBehavior: Components.NoTargetBehavior.StayStill });
			addComponent(entity, Components.Tagger);
			addComponent(entity, Components.Consumable, { type: Components.ConsumableType.Interferon });
			break;

		case GameElement.CAPSID: {
			entity = world.createEntity();
			addComponent(entity, Components.Transform, { position: params.position });
			addComponent(entity, Components.Consumable, { type: Components.ConsumableType.CapsidProtein });
			addComponent(entity, Components.NonPhysicsProtein);
			addComponent(entity, Components.Cytokine);
			addComponent(entity, Components.Renderable, {
				sprite: undefined,
				assetLocation: cellLocations.capsid,
				depthAssetLocation: depthLocations.capsid,
				glowAssetLocation: glowLocations.capsid
			});
			break;
		}

		default: //(this is kind of hack, not a real game element but needs to be in switch statement for completeness)
			entity = createVirus(world, params.position);
	}

	//This kind of sucks, probably should get passed in to all the constructors?
	if (params.rotation) {
		const physics = entity.getComponent(Components.Physics);
		if (physics) physics.initialRotation = params.rotation;
	}

	if (params.entityParameters && params.entityParameters.initialPush) {
		const physics = entity.getComponent(Components.Physics);
		if (physics) physics.initialPush = new vec2(params.entityParameters.initialPush);
	}

	if (params.promoted) {
		entity.addComponent(Components.Promoted);
	}

	//Low priority targeting adds a targeting component, but on client this is done after client targeting (in aim system)
	//So it may be possible for the component to not be added yet when the clientTargeting system needs it. For this reason,
	//we add an empty targeting here in advance, so that the clientTargeting system can always find it.
	//TODO: Remove this along with LowPriorityTarget stuff when fixing targeting for real
	if (entity.hasComponent(Components.LowPriorityTarget) && !entity.hasComponent(Components.Targeting)) {
		addComponent(entity, Components.Targeting);
	}

	if (params.entityParameters && params.entityParameters.copySourceCompElement !== undefined) {
		addComponent(entity, Components.CopyBuffs, { sourceId: params.entityParameters.copySourceCompElement.id });
	}

	if (Debug.KeepTraceLogs) {
		entityTrace(entity, `Constructed as type: ${GameElement[params.type]}`);
	}

	entity.addComponent(Components.ElementInfo, { id, type: params.type, category, creatorId });

	return entity;

	function addLifespan(entity: Entity, rng: Rng) {
		const lifetimeRng = rng.next() * Timings.VirusExpirationSecondPadding * (1 / PhysicsConstants.StepRate);
		addComponent(entity, Components.TimedLifespan, { stepsToLive: Timings.VirusExpirationSteps + lifetimeRng, firstStep: getStepNumber() });
	}

	function createImmuneCell(world: World, pos: vec2, weightClass = WeightClass.Normal) {
		const { size, shapeType } = lookupGameElementShapeAndSize(params.type)
		const entity = world.createEntity();
		addComponent(entity, Components.Physics, {
			initialPosition: pos,
			baseSize: size,
			size: size.copy(),
			shapeType,
			weightClass
		});
		addComponent(entity, Components.Renderable, {
			sprite: undefined,
			assetLocation: cellLocations[assetName],
			depthAssetLocation: depthLocations[assetName],
			membraneAssetLocation: membraneLocations[assetName],
			glowAssetLocation: glowLocations[assetName],
		});
		addComponent(entity, Components.Antibody);
		addComponent(entity, Components.Attacker);
		addComponent(entity, Components.Health, { hp: stat('health') });
		addComponent(entity, Components.Hoverable);

		const ciliaLocation = ciliaLocations[assetName];
		if (ciliaLocation !== undefined) {
			addComponent(entity, Components.Cilia, { location: ciliaLocation });
		}
		return entity;
	}

	function createBloodCell(world: World, pos: vec2) {
		const shapeType = entityHeight !== entityWidth ? Components.ColliderShape.Box : Components.ColliderShape.Circle;
		const w = pixelsToMeters(entityWidth);
		const h = pixelsToMeters(entityHeight);
		const entity = world.createEntity();

		addComponent(entity, Components.Physics, {
			initialPosition: pos,
			baseSize: new vec2([w, h]),
			size: new vec2([w, h]),
			shapeType,
			weightClass: WeightClass.Heavy
		});
		addComponent(entity, Components.Renderable, {
			sprite: undefined,
			assetLocation: cellLocations.bloodcell,
			membraneAssetLocation: membraneLocations.bloodcell,
			depthAssetLocation: depthLocations[assetName],
			glowAssetLocation: glowLocations[assetName],
		});
		addComponent(entity, Components.BloodCell);
		addComponent(entity, Components.Health, { hp: 1 });
		addComponent(entity, Components.Attacker, { damage: 999, damageType: Components.DamageType.BloodCellCollision });
		addComponent(entity, Components.HexTileable);
		addComponent(entity, Components.ArenaLimit, { maxDistance: ShopConstants.PlacementRadius, forcePerExcessDistance: 0.05 });
		return entity;
	}

	function createVirus(world: World, pos: vec2) {
		const shapeType = entityHeight !== entityWidth ? Components.ColliderShape.Box : Components.ColliderShape.Circle;
		const w = pixelsToMeters(entityWidth);
		const h = pixelsToMeters(entityHeight);
		const entity = world.createEntity();
		addComponent(entity, Components.Physics, {
			initialPosition: pos,
			baseSize: new vec2([w, h]),
			size: new vec2([w, h]),
			shapeType,
		});
		addComponent(entity, Components.Renderable, {
			sprite: undefined,
			assetLocation: location,
			depthAssetLocation: depthLocations[assetName],
			glowAssetLocation: glowLocations[assetName],
		});
		addComponent(entity, Components.Virus);
		addComponent(entity, Components.Attacker);
		addComponent(entity, Components.Health, { hp: stat('health') });
		addLifespan(entity, rng);
		addComponent(entity, Components.ArenaLimit, { maxDistance: Multiplayer.PlayerTerritoryRadius * 2, minDistance: Multiplayer.PlayerTerritoryRadius * 0.01, forcePerExcessDistance: 1 });
		addComponent(entity, Components.OnDeath, {
			effect: {
				component: freezeComponent(Components.SpawnOnce, { spawns: [{ type: GameElement.CAPSID }] }),
				targetSelector: { type: TargetType.Self },
				scope: TriggerScope.OneShot
			}
		});
		addComponent(entity, Components.Hoverable);
		const spawnLane = params.entityParameters?.spawnLane;
		if (spawnLane !== undefined) {
			addComponent(entity, Components.LaneInfo, { lane: spawnLane });
		}
		return entity;
	}

}

//TODO, just move this stuff into the switch statement
function createExplosion(world: World, position: vec2, range: number = 8) {
	const entity = world.createEntity()
	const duration = range * PhysicsConstants.ExplosianPropagationSpeed;
	addComponent(entity, Components.Transform, { position });
	addComponent(entity, Components.AoE, { currentRadius: 1, force: 600, radiusPerSecond: range / duration, durationRemaining: duration, targetFilter: CollisionCategory.Virus | CollisionCategory.Virophage | CollisionCategory.Antibody });
	addComponent(entity, Components.Attacker, { damage: 1, damageType: Components.DamageType.Explosion });
	addComponent(entity, Components.SoundSource, { sound: Sound.Explosion });
	return entity;
}

function createForceExplosion(world: World, position: vec2, creatorSyncId: number, params?: EntityParameters) {
	const entity = world.createEntity()
	const creator = getEntityById(creatorSyncId);
	const entityFilter = new Set<number>();
	if (creator) {
		entityFilter.add(creator.id);
	}
	const range = params?.effectRadius || 8;
	const duration = range * PhysicsConstants.ExplosianPropagationSpeed;
	addComponent(entity, Components.Transform, { position });
	addComponent(entity, Components.AoE, {
		currentRadius: 1,
		radiusPerSecond: range / duration,
		durationRemaining: duration,
		force: 400,
		targetFilter: params?.spawnTargetFilter || undefined,
		entityFilter
	});
	return entity;
}

function createBullet(world: World, position: vec2, target: vec2 | null, creatorSyncId?: number | undefined, health: number = 1, size: number = 0.5) {
	const entity = world.createEntity();
	addComponent(entity, Components.Cytokine);
	addComponent(entity, Components.Attacker, { damage: health });
	addComponent(entity, Components.Health, { hp: health });
	if (target !== null) {
		//addComponent(entity, Components.AimForPosition, { target });
		addComponent(entity, Components.AimForDirection, { fixedDirection: target });
		addComponent(entity, Components.UnstoppableMotion, { speed: 1000 }); //go as fast as possible, box2d will limit it in practice
	}
	addComponent(entity, Components.Renderable, {
		sprite: undefined,
		assetLocation: cellLocations.bullet,
		depthAssetLocation: depthLocations.bullet,
		glowAssetLocation: glowLocations.bullet,
	});
	addComponent(entity, Components.Bullet, { creatorSyncId });
	addComponent(entity, Components.Consumable, { type: Components.ConsumableType.Bullet })
	addComponent(entity, Components.SoundSource, { sound: Sound.Shoot });
	addComponent(entity, Components.Physics, {
		initialPosition: position,
		size: uniformVec2(size),
		baseSize: uniformVec2(0.5),
		isHighSpeed: true
	});
	return entity;
}

function createHealWave(world: World, position: vec2, creatorSyncId: number, params?: EntityParameters) {
	const entity = world.createEntity();
	const creator = getEntityById(creatorSyncId);
	//const entityFilter = creator ? [creator] : [];
	const entityFilter = new Set<number>();
	if (creator) {
		entityFilter.add(creator.id);
	}
	const duration = 0.5;
	const range = params?.effectRadius || 8;
	const healing = params?.hp || 1;
	addComponent(entity, Components.Transform, { position });
	addComponent(entity, Components.AoE, { radiusPerSecond: range / duration, durationRemaining: duration, targetFilter: CollisionCategory.Antibody, entityFilter });
	addComponent(entity, Components.Healer, { healing });
	//addComponent(entity, Components.Renderable);
	addComponent(entity, Components.SoundSource, { sound: Sound.Healwave });
	return entity;
}

function createHarmWave(world: World, position: vec2, creatorSyncId: number, params?: EntityParameters) {
	const entity = world.createEntity();
	const creator = getEntityById(creatorSyncId);
	//const entityFilter = creator ? [creator] : [];
	const entityFilter = new Set<number>();
	if (creator) {
		entityFilter.add(creator.id);
	}
	const duration = 0.1;
	const range = params?.effectRadius || 4;
	const harm = params?.hp || 1;
	addComponent(entity, Components.Transform, { position });
	addComponent(entity, Components.AoE, { radiusPerSecond: range / duration, durationRemaining: duration, targetFilter: CollisionCategory.Antibody, entityFilter });
	addComponent(entity, Components.Attacker, { damage: harm, damageType: Components.DamageType.AoEHarm });
	addComponent(entity, Components.SoundSource, { sound: Sound.WaterPlop }); //TODO sound effects for this and force wave?
	return entity;
}

export function lookupGameElementShapeAndSize(type: GameElement) {
	const info = ElementDisplayInfo.get(type);
	const assetName = info ? info.asset : "bloodcell";
	const location = cellLocations[assetName] as AssetLocation;
	const entityWidth = location.width;
	const entityHeight = location.height;
	const shapeType = entityHeight !== entityWidth ? Components.ColliderShape.Box : Components.ColliderShape.Circle;
	const w = pixelsToMeters(entityWidth);
	const h = pixelsToMeters(entityHeight);
	return { size: new vec2([w, h]), shapeType };
}